import { h, Component } from 'preact';
import $ from 'utils/net';
import OriginalConfig from './config';
import { makeReactive } from '../utils/reactive-store';

const Config = OriginalConfig as any as {
    softionAPI: string;
    publickey: string;
    lang: string;
};

interface BaseNode {
    id: string;
    name: string;
    order: number;
    parent?: string;
    sub?: Record<string, any>;
}

interface Tournament extends BaseNode {
    _id: string | number;
    label: string;
    altdesc: string | null;
    'next-event': number;
    code?: string;
}

interface Category extends BaseNode {
    _id: string | number;
    label: string;
    altdesc: string | null;
    'next-event': number;
    sub: Record<string, Tournament>;
}

interface Sport extends BaseNode {
    _id: string | number;
    count: number;
    category: 'antepost' | 'regular';
    'next-event': number;
    'next-list': {
		"8hour": Record<string, number>,
		"3hour": Record<string, number>,
		"1hour": Record<string, number>,
	} | null;
    'filter-count': Record<string, number>;
    sub: Record<string, Category>;
}

interface Outcome {
    id: string;
    name: string;
    value: number;
    order?: number | string;
}

interface Market {
    id: string;
    name: string;
    legatura: string;
    handicap: string | null;
    categories?: number[];
    order?: number;
    description?: string | null;
    sub: Record<string, Outcome>;
}

interface SportEvent {
    id: string;
    away: string | null;
    home: string;
    providercode: string;
    teamscode: { home: string; away: string };
    short: string | number;
    oddscount: number;
    parent: string;
    sport: string;
    special: string;
    date: number;
    phantom: { enabled: boolean; base: string | null };
    sub: Record<string, Market>;
    categories?: {id: string|number, name: string}[];
}

const isAntepostId = (id: string | number): boolean => false && (id as number) >= 100 && (id as number) <= 999;

interface WithSub {
	sub?: Record<string, WithSub>
}

const subToMap = (menu: WithSub): Record<string, WithSub> => {
	const result: Record<string, any> = {};
	const mapIt = (_element: Record<string, any>): void => {
		for (let id in _element) {
			const element = _element[id];
			result[element.id] = element;
			if (element.sub)
				mapIt(element.sub)
		}
	}
	mapIt(menu);
	return result;
}

const normalizeMenu = (data: any): Record<string, Sport> =>
	Object.entries(data.schedules as Record<string, any>).reduce((T, [name, S]) => {
		let tmpCats: Record<string, Category> = {};
		return Object.assign(T, {
			['/sport/' + S.id]: {
				_id: S.id,
				id: '/sport/' + S.id,
				name: !!S.trans ? S.trans : name,
				count: S.count,
				order: S.order,
				category: isAntepostId(S.id) ? 'antepost' : 'regular',
				sub: tmpCats = Object.entries(S.sub as Record<string, any>).reduce((TC, [catName, R]) => {
					let tmpTours: Record<string, Tournament> = {};
					return Object.assign(TC, {
						['/category/' + S.id + '/' + R.idPal]: {
							_id: R.idPal,
							id: '/category/' + S.id + '/' + R.idPal,
							name: !!R.trans ? R.trans : catName,
							label: catName,
							altdesc: R.altdesc ? R.altdesc : null,
							order: R.order || 0,
							sub: tmpTours = Object.entries(R.sub as Record<string, any>).reduce((TT, [tourName, t]) => Object.assign(TT, {
								['/tournament/' + S.id + '/' + R.idPal + '/' + t.idMan]: {
									_id: t.idMan,
									id: '/tournament/' + S.id + '/' + R.idPal + '/' + t.idMan,
									order: t.order || 0,
									name: !!t.trans ? t.trans : tourName,
									label: tourName,
									altdesc: t.altdesc ? t.altdesc : null,
									'next-event': (t.mintime || Infinity) * 1000,
									parent: '/category/' + S.id + '/' + R.idPal,
									code: t.code // Questo è il codice BC del torneo. Serve per le icone torneo
								}
							}), {} as Record<string, Tournament>),
							'next-event': Math.min(...Object.values(tmpTours).map(E => E['next-event'])),
							parent: '/sport/' + S.id
						}
					})
				}, {} as Record<string, Category>),
				'next-event': Math.min(...Object.values(tmpCats).map(E => E['next-event'])),
				'next-list': !!data.next_events[name] ? data.next_events[name] : null,
				'filter-count': S['filter-count'] || { '24h': 0 }
			}
		});
	}, {} as Record<string, Sport>);

const dateToTs = (str: string): number => ((_): number => new Date([_[1], _[0], _[2]].join('/')).getTime())(str.split('/'));

const extractCategories = (marketCategories: {id: number|string, nome: string}[], availableMarkets: {categoria: number[], scommessa: number, sort: string, desc_scommessa: string}[]) => {
	return Object.values(marketCategories).filter(category => category.nome.toLowerCase().indexOf('skin-view') < 0).reduce((c, R, i) =>
		Object.assign(c, {
			[R.id]: Object.assign({
				order: i,
				name: R.nome,
				sub: availableMarkets.filter(M => M.categoria.indexOf(parseInt(String(R.id))) >= 0).reduce((sc, M) =>
					Object.assign(sc, {
						[M.scommessa]:
							Object.assign({
								order: parseInt(M.sort),
								id: M.scommessa,
								name: M.desc_scommessa
							}, M)
					}),
					{}
				) as Record<number, {order: number, id: number, name: string}>
			}, R)
		}), 
		{}
	) as Record<number, {order: number, name: string, sub: Record<number, {order: number, id: number, name: string}>}>;
}

const tournamentPath = (tid: string): string[] => tid.split('/').slice(2);

const normalizeEventsList = (data: any, tid: string) => {
	const [sportId, regionId, tournamentId] = tournamentPath(tid);
	const market = {
		...data.market[regionId][tournamentId],
		name: data.market[regionId][tournamentId].desc_scommessa
	};
	let events: Record<string, SportEvent>;

	const isPhantom = market.phantom.length == undefined;
	const eventsOrder = (market.sort as any[]).map((E, i) => ({ id: E as string, name: market.esiti[i] as string }));
	const isFakePhantom = anyOddHasCustomName(data.events);

	const tournament = {
		id: tid,
		name: data.name as string,
		displayRule: !isFakePhantom ? eventsOrder : buildFakeDisplayRule(data.events),
		market: {
			id: market.scommessa as number,
			name: market.name as string,
			divisible: (market.divisible || isFakePhantom) as boolean,
			sub: isPhantom && Object.entries(market.phantom_names as Record<string, string>).reduce((c, [id, name]) => ({
				...c,
				[id]: {
					id,
					name
				}
			}), {}) as Record<number, {id: number, name: string}>
		},
		availableMarkets: (data.market.availableMarkets as any[]).map(M => ({
			id: M.scommessa as number,
			name: M.desc_scommessa as string
		})),
		categories: extractCategories(
			Object.keys(data.marketCategories).length ? data.marketCategories : [{ id: 0, nome: 'Altro' }],
			(data.market.availableMarkets as any[]).map(M => Object.assign({}, M, {
				categoria: JSON.parse(`[${M.categoria}]`) as number[]
			}))
		),
		sub: events =
			Object.entries((Object.values(data.events) as any)[0].eventi as Record<string, any>).map(([id, G]) => [id.split('|')[1], G]).reduce((T, [id, G]) => Object.assign(T, {
				['/event/' + id]: {
					id: '/event/' + id,
					away: G.away ? G.away.replace(/—/g, '-') : null,
					home: G.home.replace(/—/g, '-'),
					providercode: G.code,
					teamscode: !!G.teamcode ? G.teamcode : { home: '', away: '' },
					short: G.id,
					oddscount: G.oddscount,
					parent: tid,
					sport: sportId,
					special: G.special,
					date: (new Date(G.date_ts * 1000).getTime()) / 1000,
					phantom: {
						enabled: !!isPhantom,
						base: (G.special || '').split(' ')[1]
					},
					sub: Object.assign({}, !isPhantom
						? {
							['/market/' + id + '/' + market.scommessa]: {
								id: '/market/' + id + '/' + market.scommessa,
								name: market.desc_scommessa,
								legatura: G.legature[market.scommessa],
								handicap: G.specials[market.scommessa] || false,
								sub: Object.entries(G.quote[market.scommessa] as Record<string, string>).reduce((c, [oid, odd]) => Object.assign(c, {
									['/odd/' + id + '/' + market.scommessa + '/' + oid]: {
										id: '/odd/' + id + '/' + market.scommessa + '/' + oid,
										value: (odd == '-') ? 0 : parseFloat(odd),
										name: maybeCustomName(G.override_outcomes, market.scommessa, oid) || // O prendi il nome custom associato alla quota
											market.esiti[parseInt(oid) - 1] // Prende il nome a partire dalla lista completa di esiti
									}
								}), {})
							}
						}
						: Object.entries(market.phantom as Record<string, any>).reduce((c: any, [mid, _inutile]) => {
							c['/market/' + id + '/' + mid] = {
								id: '/market/' + id + '/' + mid,
								name: market.phantom_names[mid],
								legatura: G.legature[mid],
								handicap: G.specials[mid] || false,
								sub: Object.entries(G.quote[mid] as Record<string, string>).reduce((sc, [eid, odd]) => {
									sc['/odd/' + id + '/' + mid + '/' + eid] = {
										id: '/odd/' + id + '/' + mid + '/' + eid,
										value: (odd == '-') ? 0 : parseFloat(odd),
										name: maybeCustomName(G.override_outcomes, mid, eid) || // O prendi il nome custom associato alla quota
											(eventsOrder.find(r => r.id == mid + '.' + eid) || { name: null }).name // O prendi il nome usando le associazioni precalcolate
									}
									return sc;
								}, {} as any)
							}
							return c;
						}, {} as any)
					)
				}
			}), {} as Record<string, SportEvent>),
		hasBase: !!Object.values(events).find(E => !!E.phantom.base)
	};

	return tournament;
}

const maybeCustomName = (customNames: Record<string, Record<string, string>>, marketid: string | number, outcomeid: string | number): string | null => {
	if (!customNames)
		return null;
	if (!customNames[marketid] || !customNames[marketid][outcomeid])
		return null;
	return customNames[marketid][outcomeid]
}

const anyOddHasCustomName = (events: any): boolean => Object.values((Object.values(events)[0] as any).eventi as Record<string, any>).some(event => !!event.override_outcomes)

const buildFakeDisplayRule = (events: any): {id: string, name: string}[] => {
	const listaEventi = Object.values((Object.values(events)[0] as any).eventi as Record<string, any>);
	let longestCustomRule: string[] = [];
	listaEventi.forEach(event => {
		const eventCustomRule = Object.entries(event.override_outcomes as Record<string, any>).flatMap(([marketId, outcomesNames]) => {
			return Object.keys(outcomesNames).map(outcomeId => marketId + '.' + outcomeId)
		})
		if (longestCustomRule.length < eventCustomRule.length)
			longestCustomRule = eventCustomRule;
	});
	return longestCustomRule.map(id => ({ id, name: '*' }));
}

const normalizeEventMarkets = (data: any, eid: string): Record<string, Market> => Object.entries(data.eventoquote as Record<string, any>).filter(([k, M]) => Object.keys(M.esiti).length > 0).reduce(
	(c, [mId, M]) => Object.assign({}, c, {
		[eid.replace('event', 'market') + '/' + mId]: {
			id: eid.replace('event', 'market') + '/' + mId,
			name: M.desc,
			categories: (M.categorie || ['-1']).map((id: string) => parseInt(id)),
			legatura: Object.values(M.esiti as Record<string, any>)[0].legatura,
			order: M.order,
			description: M.description || null,
			sub: Object.entries(M.esiti as Record<string, any>).reduce((sc, [oId, O]) => Object.assign({}, sc, {
				[eid.replace('event', 'odd') + '/' + mId + '/' + oId]: {
					id: eid.replace('event', 'odd') + '/' + mId + '/' + oId,
					name: O.desc,
					value: parseFloat(O.quota == '-' ? 0 : O.quota),
					order: oId
				}
			}), {})
		}
	}), {} as Record<string, Market>);

const NStorage = {
	storage: {} as Record<string, any>,
	storeData: (sub: Record<string, any>) => {
		const newKeys = subToMap(sub);
		NStorage.storage = Object.assign(NStorage.storage, newKeys);
	},
	get: (key: string): any => NStorage.storage[key],
	removeWhere: (fn: (k: string, v: any) => boolean) => NStorage.storage = Object.entries(NStorage.storage).filter(([k, v]) => !fn(k, v)).reduce((T, [k, v]) => Object.assign(T, { [k]: v }), {}),
	remove: (key: string) => NStorage.removeWhere(k => key == k),
	performGC: (key: string) => NStorage.removeWhere((k, v) => !v || (v.parent && !NStorage.storage[v.parent]))
};

type EventBusCallback = (tag: string, data: any, unsubscribe: () => void) => void;

const EventBus = {
	events: {} as Record<string, EventBusCallback[]>,
	subscribe: (tag: string, cb: EventBusCallback) => {
		EventBus.events[tag] = (EventBus.events[tag] || []).filter(C => C != cb).concat(cb);
	},
	unsubscribe: (tag: string, cb: EventBusCallback) => {
		EventBus.events[tag] = (EventBus.events[tag] || []).filter(C => C != cb);
	},
	notify: (tag: string, data: any) => {
		(EventBus.events[tag] || []).forEach(cb => cb(tag, data, () => EventBus.unsubscribe(tag, cb)));
	},
	monosubscribe: (tag: string, cb: (tag: string, data: any) => void) => {
		EventBus.events[tag] = (EventBus.events[tag] || []).concat((t, d, unsubscribe) => {
			unsubscribe();
			cb(t, d);
		});
	}
};

const safe = <T>(fn: () => T, or: T): T => {
	try { return fn(); } catch (e) { return or; }
}

export default class PrematchFeedService extends Component {
	private static readonly _state = makeReactive({
		lang: null as string,
		bonus: [] as any[],
	});
	static get lang() {return this._state.lang}
	static set lang(v) {this._state.lang = v}
	static get bonus() {return this._state.bonus}
	static set bonus(v) {this._state.bonus = v}
	
	static menu: Record<string, Sport> | false = false;
	static NStorage = NStorage;
	static EventBus = EventBus;
	static EventSearchResult: any[] | false = false;
	static mainTournaments: string[] = [];
	static normalizeEventMarkets = normalizeEventMarkets;

	static searchEvent(input: string, cb: (res?: any[]) => void): void {
		if (input.trim().length >= 3) {
			$.post(
				Config.softionAPI + '/schedina/suggest?json',
				{ body: { suggest: input } },
				(err: any, data: any) => {
					if (err) {
						PrematchFeedService.EventSearchResult = false;
						return console.error(err);
					}

					const parsedData = JSON.parse(data);
					PrematchFeedService.EventSearchResult = Object.values(parsedData.info || {});

					let events: Record<string, any> = {};
					(PrematchFeedService.EventSearchResult as any[]).forEach(event => {
						const [sport, palinsesto, manifestazione, evento] = event.slug.split('-');
						event.sport = sport;
						event.palinsesto = palinsesto;
						event.manifestazione = manifestazione;
						event.evento = evento;
						
						events['/event/' + event.evento] = {
							id: '/event/' + event.evento,
							home: event.team_casa,
							away: event.team_ospite,
							parent: '/tournament/' + [event.sport, event.palinsesto, event.manifestazione].join('/'),
							sport: event.sport,
							short: event.indice_ricerca,
							date: new Date(event.data_evento).getTime() / 1000,//ts in secondi
							sub: {}
						};
					});

					NStorage.storeData(events);
					cb(PrematchFeedService.EventSearchResult as any[]);
				}
			);
		} else {
			PrematchFeedService.EventSearchResult = false;
			cb();
		}
	}

	static getEventMarkets(shortid: string | number, cb: (data: any) => void): void {
		$.post(
			Config.softionAPI + '/schedina/getMarkets?json',
			{ body: { evento: shortid } },
			(err: any, data: any) => {
				if (err) {
					return console.error(err);
				}

				cb(JSON.parse(data));
			}
		);
	}

	static getMenu(cb: (tag: string, data: any) => void): void {
		if (PrematchFeedService.menu)
			return cb('menu', PrematchFeedService.menu);
		else
			return EventBus.monosubscribe('menu', cb);
	}

	static callBonusList(): void {
		$.getJSON(Config.softionAPI + '/prematch/bonuslist?json' + (!!Config.publickey ? '&public=' + Config.publickey : ''), (err: any, data: any) => {
			if (err)
				return;
			PrematchFeedService.bonus = (data.bonus as any[]).map(B => ({
				import: {
					min: parseFloat(B.minimo_scommesso),
					max: Infinity
				},
				events: {
					min: parseInt(B.minimo_eventi),
					max: parseInt(B.massimo_eventi)
				},
				odd: {
					min: parseFloat(B.minimo_quota)
				},
				percentage: parseFloat(B.percentuale_bonus) / 100
			}));
			//console.info(PrematchFeedService.bonus);
			return EventBus.notify('bonus', PrematchFeedService.bonus);
		});
	}

	static callMenu(): void {
		$.getJSON(Config.softionAPI + '/prematch/main?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : ''), (err: any, data: any) => {
			if (err)
				return;
			PrematchFeedService.menu = normalizeMenu(data);
			NStorage.storeData(PrematchFeedService.menu as Record<string, Sport>);
			PrematchFeedService.mainTournaments = (data.favourites as string[] || []).filter(x => !!x).map(id => `/tournament/${id}`).filter(tId => PrematchFeedService.NStorage.get(tId));
			return EventBus.notify('menu', PrematchFeedService.menu);
		});
	}

	static callTournamentMono(tPath: string, options: any, cb: (tag: string, data: any) => void) {
		EventBus.monosubscribe(tPath + '-mono', cb);
		return $.getJSON(
			Config.softionAPI
			+ '/prematch/lista/' + tPath.split('/').slice(2).concat(options.market || []).join('/')
			+ '?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : '')
			+ (options.isphantom === false ? '&p=0' : ''),
			(err: any, data: any) => {
				if (err)
					return console.error(err);
				// todo store in menu
				const tournament = normalizeEventsList(data, tPath);

				NStorage.storeData(tournament.sub);
				EventBus.notify(tPath + '-mono', tournament);
			}
		)
	}

	static callTournament(tPath: string, options: any, cb: EventBusCallback | false = false) {
		if (!!cb)
			EventBus.subscribe(tPath, cb);
		return $.getJSON(
			Config.softionAPI
			+ '/prematch/lista/' + tPath.split('/').slice(2).concat(options.market || []).join('/')
			+ '?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : '')
			+ (options.isphantom === false ? '&p=0' : ''),
			(err: any, data: any) => {
				if (err)
					return console.error(err);
				// todo store in menu
				const tournament = normalizeEventsList(data, tPath);

				NStorage.storeData(tournament.sub);
				EventBus.notify(tPath, tournament);
			}
		)
	}

	static callNextEvents(sport: string, hour: string | number, options: any = {}, cb: EventBusCallback | false = false) {
		let eventTag = sport + '/' + hour + 'hour';
		// /tournament/1/19/113

		if (!!cb)
			EventBus.subscribe(eventTag, cb);

		return $.getJSON(
			Config.softionAPI + '/prematch/lastminute/' + sport.split('/')[2] + '/' + hour + '/?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : ''),
			(err: any, data: any) => {
				if (err)
					return console.error(err);

				let tournaments: Record<string, any> = {};

				Object.entries((Object.entries(data.events)[0] as any[])[1].eventi as Record<string, any>).map(([id, e]) => {
					const categoryId = sport.replace('sport', 'category') + '/' + id.split('|')[0];
					let tPath = sport.replace('sport', 'tournament') + '/' + id.split('|')[0] + '/' + e.tournament_id;
					let tournamentNameSplit = e.tournament_name.split(' - ', 2);
					let tournamentName = !tournamentNameSplit[1] ? tournamentNameSplit[0] : tournamentNameSplit[1];
					let categoryName = tournamentNameSplit[0];
					let sportName = (Object.entries(data.events)[0] as any[])[1].desc_sport;

					// Togli dalla lista gli eventi già iniziati
					if (e.date_ts * 1000 < Date.now()) {
						return;
					}

					tournaments[tPath] = tournaments[tPath] || {
						...data,
						id: tPath,
						parent: categoryId,
						name: e.tournament_name,
						events: {
							[tournamentName]: {
								desc_palinsesto: categoryName, // È inutile, non sovrascrive nulla
								desc_sport: sportName, // È inutile, non sovrascrive nulla
								eventi: {},
								manifestazione: e.tournament_id,
								palinsesto: id.split('|')[0],
								sport: sport.split('/')[2]
							}
						},
						market: {
							availableMarkets: [],
							[id.split('|')[0]]: {
								[e.tournament_id]: { ...data.market }
							}
						},
						marketCategories: []
					};

					tournaments[tPath].name = e.tournament_name;
					tournaments[tPath].events[tournamentName].eventi[id] = e;
					// console.info(tournaments);
				});

				Object.entries(tournaments).map(([tPath, tournament]) => {
					tournaments[tPath] = normalizeEventsList(tournament, tPath);
					NStorage.storeData(tournaments[tPath].sub);
				});

				EventBus.notify(eventTag, tournaments);
			}
		)
	}

	static callMostBetEvents(sport: string, cb: ((tag: string, data: any) => void) | false = false) {
		let eventTag = sport + '/mostbet';

		if (!!cb) {
			EventBus.monosubscribe(eventTag, cb);
		}

		return $.getJSON(
			Config.softionAPI + '/scommesse/mostplayed?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : ''),
			(err: any, data: any) => {
				if (err || !data.mostbetevents) {
					return console.error(err);
				}

				let tournaments: Record<string, any> = {};

				Object.entries((Object.entries(data.mostbetevents)[0] as any[])[1].eventi as Record<string, any>).map(([id, e]) => {
					const categoryId = sport.replace('sport', 'category') + '/' + id.split('|')[0];
					let tPath = sport.replace('sport', 'tournament') + '/' + id.split('|')[0] + '/' + e.tournament_id;
					let tournamentNameSplit = e.tournament_name.split(' - ', 2);
					let tournamentName = !tournamentNameSplit[1] ? tournamentNameSplit[0] : tournamentNameSplit[1];
					let categoryName = tournamentNameSplit[0];
					let sportName = (Object.entries(data.mostbetevents)[0] as any[])[1].desc_sport;

					tournaments[tPath] = tournaments[tPath] || {
						...data,
						id: tPath,
						parent: categoryId,
						name: e.tournament_name,
						events: {
							[tournamentName]: {
								desc_palinsesto: categoryName, // È inutile, non sovrascrive nulla
								desc_sport: sportName, // È inutile, non sovrascrive nulla
								eventi: {},
								manifestazione: e.tournament_id,
								palinsesto: id.split('|')[0],
								sport: sport.split('/')[2]
							}
						},
						market: {
							availableMarkets: [],
							[id.split('|')[0]]: {
								[e.tournament_id]: { ...data.market }
							}
						},
						marketCategories: []
					};

					tournaments[tPath].name = e.tournament_name;
					tournaments[tPath].events[tournamentName].eventi[id] = e;
				});

				Object.entries(tournaments).map(([tPath, tournament]) => {
					tournaments[tPath] = normalizeEventsList(tournament, tPath);
					NStorage.storeData(tournaments[tPath].sub);
				});

				EventBus.notify(eventTag, tournaments);
			}
		)
	}

	static callMostPlayedEvents(sport: string, cb: ((tag: string, data: any) => void) | false = false) {
		let eventTag = sport + '/mostplayed';

		if (!!cb) {
			EventBus.monosubscribe(eventTag, cb);
		}

		return $.getJSON(
			Config.softionAPI + '/scommesse/mostplayed?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : ''),
			(err: any, data: any) => {
				if (err || !data.events) {
					return console.error(err);
				}

				let tournaments: Record<string, any> = {};

				Object.entries((Object.entries(data.events)[0] as any[])[1].eventi as Record<string, any>).map(([id, e]) => {
					const categoryId = sport.replace('sport', 'category') + '/' + id.split('|')[0];
					let tPath = sport.replace('sport', 'tournament') + '/' + id.split('|')[0] + '/' + e.tournament_id;
					let tournamentNameSplit = e.tournament_name.split(' - ', 2);
					let tournamentName = !tournamentNameSplit[1] ? tournamentNameSplit[0] : tournamentNameSplit[1];
					let categoryName = tournamentNameSplit[0];
					let sportName = (Object.entries(data.events)[0] as any[])[1].desc_sport;

					tournaments[tPath] = tournaments[tPath] || {
						...data,
						id: tPath,
						parent: categoryId,
						name: e.tournament_name,
						events: {
							[tournamentName]: {
								desc_palinsesto: categoryName, // È inutile, non sovrascrive nulla
								desc_sport: sportName, // È inutile, non sovrascrive nulla
								eventi: {},
								manifestazione: e.tournament_id,
								palinsesto: id.split('|')[0],
								sport: sport.split('/')[2]
							}
						},
						market: {
							availableMarkets: [],
							[id.split('|')[0]]: {
								[e.tournament_id]: { ...data.market }
							}
						},
						marketCategories: []
					};

					tournaments[tPath].name = e.tournament_name;
					tournaments[tPath].events[tournamentName].eventi[id] = e;
				});

				Object.entries(tournaments).map(([tPath, tournament]) => {
					tournaments[tPath] = normalizeEventsList(tournament, tPath);
					NStorage.storeData(tournaments[tPath].sub);
				});

				EventBus.notify(eventTag, tournaments);
			}
		)
	}

	static callHilightedEventsWidget(options: any = {}, cb: ((data: any) => void) | false = false) {
		return $.getJSON(
			Config.softionAPI + '/widgets/matchreel?f=init' + (!!Config.publickey ? '&public=' + Config.publickey : ''),
			(err: any, data: any) => {
				if (err)
					return console.error(err);
				const NAMES: Record<string, string> = { "Home": "1", "draw": "X", "Away": "2" };
				// todo store in menu
				const events = Object.entries(data.event as Record<string, any>).map(([id, e]) => ({
					id: '/event/' + e.evento,
					home: e.home_name,
					away: e.away_name,
					home_id: e.home_id,
					away_id: e.away_id,
					teams: {
						home: {
							id: '/team/' + e.evento + '/' + e.home_id,
							name: e.home_name
						},
						away: {
							id: '/team/' + e.evento + '/' + e.away_id,
							name: e.away_name
						},
					},
					parent: '/tournament/' + [e.sport, e.manifestazione, e.idman].join('/'),
					category: '/category/' + [e.sport, e.manifestazione].join('/'),
					tournament_name: e.champ,
					sport: e.sport,
					short: e.short_id,
					date: new Date(e.date).getTime() / 1000,
					sub: safe(() => Object.entries(data.odds[id] as Record<string, any>).map(([mid, market]) => ({
						id: '/market/' + [e.evento, mid].join('/'),
						name: (Object.values(market as Record<string, any>)[0] as any).market,
						legatura: (Object.values(market as Record<string, any>)[0] as any).legatura,
						categories: [-1],
						sub: Object.entries(market as Record<string, any>).map(([oid, outcome]) => ({
							id: '/odd/' + [e.evento, mid, oid].join('/'),
							name: NAMES[outcome.outcome],
							value: parseFloat(outcome.odd)
						})).reduce((c, row) => ({ ...c, [row.id]: row }), {})
					})).reduce((c, row) => ({ ...c, [row.id]: row }), {}), {})
				})).reduce((c, row) => ({ ...c, [row.id]: row }), {})

				NStorage.storeData(events);

				if (!!cb)
					cb(events);
			}
		)
	}

	static getWithParents(id: string, data: Record<string, any> = {}): Record<string, any> {
		if (!id)
			return data;
		let object = NStorage.get(id);
		if (!object)
			return data;
		let key = object.id.split('/')[1];
		if (!!data[key])
			return data;
		data[key] = object;
		return PrematchFeedService.getWithParents(object.parent, data);
	}

	static callEvent(ePath: string, options: any, cb: (tag: string, data: any) => void) { // ePath: /event/12345678
		if (!NStorage.storage[ePath])
			return;
		const event = NStorage.storage[ePath] as SportEvent;
		const path = event.parent.split('/')[3] + '/' + event.id.split('/')[2] + '/' + event.short;

		EventBus.monosubscribe(ePath, cb);
		return $.getJSON(
			Config.softionAPI + '/scommesse/eventoquote/' + path + '?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : ''),
			(err: any, data: any) => {
				const markets = normalizeEventMarkets(data, ePath);
				event.sub = markets;
				event.categories = (data.categorie as any[]).filter(category => category.name.toLowerCase().indexOf('skin-view') < 0);
				NStorage.storeData(markets);
				EventBus.notify(ePath, markets);
			}
		)
	}

	static callEventV2(ePath: string, options: any, cb: (tag: string, data: any) => void) { // ePath: /event/12345678
		if (!NStorage.storage[ePath])
			return;
		const event = NStorage.storage[ePath] as SportEvent;
		const path = event.parent.split('/')[3] + '/' + event.id.split('/')[2] + '/' + event.short;

		EventBus.monosubscribe(ePath, cb);
		return $.getJSON(
			Config.softionAPI + '/scommesse/eventoquote/' + path + '?json&l=' + Config.lang + (!!Config.publickey ? '&public=' + Config.publickey : ''),
			(err: any, data: any) => {
				const markets = normalizeEventMarkets(data, ePath);
				event.sub = markets;
				event.categories = (data.categorie as any[]).filter(category => category.name.indexOf('skin-view') < 0);
				NStorage.storeData(markets);
				EventBus.notify(ePath, event);
			}
		)
	}

	static callMarketsTranslations(lang: string, cb: (tag: string, data: any) => void): void {
		EventBus.monosubscribe('markets-lang', cb);
		$.getJSON(Config.softionAPI + '/home/common/lang/markets/fetch.php?l=' + lang, (err: any, data: any) => {
			EventBus.notify('markets-lang', new Map(Object.entries(data)));
		});
	}

	static callScheduleTranslations(lang: string): void {
		$.getJSON(Config.softionAPI + '/home/common/lang/betting/fetch.php?l=' + lang, (err: any, data: any) => {
			if (err) {
				console.error('Schedules Languages Error', err);
				return;
			}

			if (!Object.keys(data).length) {
				return;
			}

			let schedules = Object.entries(data as Record<string, string>);
			let toNotify: Record<string, any[]> = { 'sport': [], 'pal': [], 'man': [] };

			schedules.map(s => {
				let type = s[0].split('_')[1].toLowerCase().trim();

				if (!toNotify[type]) {
					return;
				}

				toNotify[type].push({
					id: s[0].split('_')[2].trim(),
					label: s[1]
				});
			});

			for (let type in toNotify) {
				EventBus.notify(type + '-lang', toNotify[type]);
			}

			EventBus.notify('schedules-lang', toNotify);
		});
	}

	static quickSearch(options: any, cb: (res: any[]) => void): void {
		return cb([]);
	}

	static exists(id: string): any {
		const [key, sport, category, tournament] = id.substring(1).split('/');
		const menu = PrematchFeedService.menu || {} as Record<string, Sport>;
		return key == 'sport' ? menu[`/sport/${sport}`] :
			key == 'category' ? safe(() => menu[`/sport/${sport}`].sub[`/category/${sport}/${category}`], null) :
				key == 'tournament' ? safe(() => menu[`/sport/${sport}`].sub[`/category/${sport}/${category}`].sub[`/tournament/${sport}/${category}/${tournament}`], null) :
					null;
	}

	static parents(id: string): any[] {
		const element = PrematchFeedService.exists(id);
		if (!element) return [];
		if (!element.parent)
			return [element];
		return [element].concat(PrematchFeedService.parents(element.parent));
	}

	componentWillMount(): void {
		PrematchFeedService.callMenu();
		PrematchFeedService.callBonusList();
	}

	render(): null {
		return null;
	}
}